import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useGetAll } from "@api/crudService";
import { Autocomplete, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormLabel, Input, Modal, ModalDialog, ModalOverflow, Stack, createFilterOptions } from "@mui/joy";
import { useDebounce } from "@uidotdev/usehooks";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import { ObjectPrimaryInfoEdit } from "./objects/ObjectPrimaryInfoEdit";
import { FieldEditTable } from "./FieldEditTable";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { authenticatedApi } from "api";
import { queryClient } from "@api/queryClient";
import { supportedQuickCreateObjects } from "@api/schema/supportedQuickCreateObjects";
import { isNumeric } from "@utils/numbers";
import { arrayUniqueByKey } from "@utils/arrayUniqueByKey";
import { morphLabelForEndpoint } from "@utils/morphTypes";

// TODO: preload the currently selected as well as whatever they type
// do NOT load all unless a flag is specifically set

function CreateModal({open, setOpen, endpoint, value, afterCreate}) {
  const details = supportedQuickCreateObjects?.[endpoint];

  let defaultValues = {};
  details?.fields?.map(field => {
    defaultValues[field.name] = '';
  });

  const firstKey = details?.fields?.[0]?.name ?? '';

  const formMethods = useForm({
    defaultValues: defaultValues,
    //{
      // [firstKey]: value,
        // ...tenant,
        // state: tenant.state_
    // },
  });
  const {
      setValue,
      reset,
      handleSubmit,
      watch,
      getValues,
      formState: { isSubmitting, errors, isValid, isDirty }
  } = formMethods;

  useEffect(() => {
    reset();
    /*
    if(isDirty) {
      return;
    }
    */

    // handle when the user has typed multiple spaces
    const secondKey = details?.fields?.[1]?.name ?? '';
    if(details?.splitSpaces && value.indexOf(' ') > -1) {
      const valSplit = value.split(' ');
      setValue(firstKey, valSplit[0]);

      if(secondKey) {
        setValue(secondKey, valSplit[1]);
      }
    } else {
      setValue(firstKey, value);
      if(secondKey) {
        setValue(secondKey, '');
      }
    }
  }, [value]);

  const onSubmit = (fields) => {
    const performUpdate = authenticatedApi.post(`/${endpoint}`, fields);
    toast.promise(performUpdate, {
        loading: `Creating...`,
        success: `The ${details.label.toLowerCase()} was saved!`,
        error: (err) => `There was an error while saving.`,
    });
    performUpdate.then((response) => {
      // console.log(response.data);
      afterCreate(response.data);
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      queryClient.invalidateQueries({ queryKey: [`searchselect_${endpoint}`] });
      queryClient.invalidateQueries({ queryKey: ['searchselect_polymorphic', endpoint] });
      reset();
      setOpen(false);
    });
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalOverflow>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <ModalDialog>
            <DialogTitle>{/* Create  */}{details?.label} Quick Add</DialogTitle>
            <Divider/>
            <DialogContent
              sx={{
                fontSize: 'sm',
              }}
            >
              Only the minimum fields are shown.<br/>Don't forget to fill in the remaining details later!
            </DialogContent>
            <Box
              sx={{
                // p: 2
                marginInline: 'var(--Divider-inset)'
              }}
            >
              <FieldEditTable fields={details?.fields ?? []}/>
            </Box>

            <DialogActions>
              <Button
                disabled={!isValid}
                type="button" // this can't be submit type or it will submit the parent form
                onClick={() => {
                  onSubmit(getValues());
                }}
              >
                Save
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => {
                    setOpen(false);
                    try {
                        // onCancel();
                    } catch(e) {
                        //
                    }
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </FormProvider>
      </form>
      </ModalOverflow>
    </Modal>
  );
}

const filter = createFilterOptions();


const SearchSelect = forwardRef(function SearchSelect({
    endpoint,
    value = null,
    valueEndpoint = null,
    hydrateWith = [],
    onBlur = null,
    onChange,
    canCreate = true,
    loadAll = false,
    onChangeOptions = null,
    ...restProps
}, ref) {
    const endpoints = useMemo(
      () => (endpoint ?? '').split(',').map(entry => entry.trim()).filter(Boolean),
      [endpoint]
    );
    const isPolymorphic = endpoints.length > 1;

    const [createOpen, setCreateOpen] = useState(false);
    const [createEndpoint, setCreateEndpoint] = useState(endpoints[0] ?? endpoint);
    const [createInitialValue, setCreateInitialValue] = useState('');

    const [open, setOpen] = useState(false);
    const [options, _unstable_setOptions] = useState(hydrateWith);
    const [isHydrating, setIsHydrating] = useState(false)

    const searchInputRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 150);

    const optionIdentity = (option) => isPolymorphic
      ? `${option?.__endpoint ?? ''}:${option?.id}`
      : `${option?.id}`;

    const setUniqueOptions = (results:any[]) => {
      // append results uniquely so we don't override or add duplicates
      let newOptions = [...options];
      results.map(entry => {
          if(!newOptions.find(opt => optionIdentity(opt) === optionIdentity(entry))) {
              newOptions.push(entry);
          }
      });

      newOptions = isPolymorphic
        ? arrayUniqueByKey('__identity', newOptions.map(opt => ({...opt, __identity: optionIdentity(opt)})))
        : arrayUniqueByKey('id', newOptions);

      if(newOptions.length) {
        _unstable_setOptions(newOptions);
      }
    }

    // set selected object after load, if needed
    useEffect(() => {
      // don't risk overloading
      if(loadAll) {
        return;
      }

      if(hydrateWith.length && options.length === 0) {
        setUniqueOptions(hydrateWith);
        return;
      }

      // this is useful for when we dont have full objects to pass
      if(value && options.length === 0) {
        let idSearch = '';
        if(
          Array.isArray(value)
          && value.length
          && isNumeric(value[0])
        ) {
          idSearch = value.join(',');
        }
        else if(isNumeric(value)) {
          idSearch = ""+value;
        }

        if(idSearch.length) {
          setIsHydrating(true);

          const endpointsToHydrate = (isPolymorphic && valueEndpoint) ? [valueEndpoint] : endpoints;

          Promise.allSettled(endpointsToHydrate.map(_endpoint => (
            authenticatedApi.get(`${_endpoint}?include=none&id=${idSearch}`).then(response => {
              setUniqueOptions(response.data.data.map(entry => ({...entry, __endpoint: _endpoint})));
            })
          ))).finally(() => {
            setIsHydrating(false);
          });
        }
      }
    }, [hydrateWith, value]);

    const { data, isLoading } = useGetAll({
        queryKey: [endpoint],
        enabled: !isPolymorphic && (loadAll || debouncedSearchTerm.length > 0),
    }, {
        per_page: '-1',
        include: restProps?.include ?? 'none',
        ...(loadAll || debouncedSearchTerm ? {
            search: loadAll ? '' : debouncedSearchTerm
        } : {}),
    });

    useEffect(() => {
        if(!data?.data) {
            return;
        }

        setUniqueOptions(data.data);
    }, [data]);

    const polymorphicQueries = useQueries({
      queries: endpoints.map(_endpoint => ({
        queryKey: ['searchselect_polymorphic', _endpoint, restProps?.include ?? 'none', loadAll ? '' : debouncedSearchTerm],
        enabled: isPolymorphic && (loadAll || debouncedSearchTerm.length > 0),
        queryFn: async () => {
          const urlParams = new URLSearchParams({
            page: '1',
            per_page: '-1',
            include: restProps?.include ?? 'none',
            search: loadAll ? '' : debouncedSearchTerm,
          });

          const response = await authenticatedApi.get(`/${_endpoint}?${urlParams}`);

          return (response?.data?.data ?? []).map(entry => ({...entry, __endpoint: _endpoint}));
        },
      })),
    });

    const polymorphicUpdatedAt = polymorphicQueries.map(query => query.dataUpdatedAt).join(',');
    const isSearching = isPolymorphic
      ? polymorphicQueries.some(query => query.isFetching)
      : isLoading;

    useEffect(() => {
      if(!isPolymorphic) {
        return;
      }

      const merged = polymorphicQueries.flatMap(query => query.data ?? []);

      if(merged.length) {
        setUniqueOptions(merged);
      }
    }, [polymorphicUpdatedAt]);

    const handleChange = (event, newValue, reason) => {
      let valueToSupply:any = restProps?.multiple === true ? [] : null;
      if(reason === 'clear') {
        onChange(valueToSupply, newValue);
        return;
      }


      let createValue = null;
      if(Array.isArray(newValue)) {
        if(
          typeof newValue[newValue.length - 1] === 'object'
          && newValue[newValue.length - 1].hasOwnProperty('inputValue')
        ) {
          createValue = newValue[newValue.length - 1];
        }

        valueToSupply = newValue;
      } else if(newValue.hasOwnProperty('inputValue')) {
        createValue = newValue;
      }

      // display create dialog
      if(createValue) {
        const inputEl = document.activeElement as HTMLElement | null; // grab it before the modal mounts
        setCreateEndpoint(createValue?.__endpoint ?? endpoints[0] ?? endpoint);
        setCreateInitialValue(createValue?.inputValue);
        setCreateOpen(true);
        setOpen(false);
        requestAnimationFrame(() => inputEl?.blur());
        return;
      }

      if(!newValue) {
          // do nothing
      }
      else if(Array.isArray(newValue)) {
        // make the newly supplied value match existing value type
        if(
          newValue.length > 1
          && typeof newValue[0] !== 'object'
          && typeof newValue[newValue.length - 1] === 'object'
        ) {
          newValue[newValue.length - 1] = newValue[newValue.length - 1].id;
        }

        valueToSupply = newValue;
      }
      else if(typeof newValue === 'string') {
          valueToSupply = newValue;
      } else if(typeof newValue === 'object') {
          if(newValue.hasOwnProperty('id')) {
              valueToSupply = newValue.id;
          } else {
              console.debug(Object.keys(newValue));
          }
      }

      onChange(valueToSupply, newValue);

      if(restProps?.multiple === true) {
        setSearchTerm('');
      }
    };

    const afterCreateNew = (createdValue) => {
      const newValue = isPolymorphic ? {...createdValue, __endpoint: createEndpoint} : createdValue;

      setUniqueOptions([newValue]);

      if (restProps?.multiple === true) {
        const current = Array.isArray(value) ? value : (value != null ? [value] : []);
        handleChange(null, [...current, newValue], 'created'); // append, don't replace
      } else {
        handleChange(null, newValue, 'created');
      }
    };

    const getOptionKey = (option) => {
      if(typeof option?.id !== 'undefined') {
        return optionIdentity(option);
      }

      // console.log('could not use id for getting the key');

      return getOptionLabel(option);
    }

    const getOptionLabel = (option) => {
        if(option === '') {
            return '';//null;
        }

        if(typeof option === 'object' && option.hasOwnProperty('inputValue')) {
            return option.title;//<><AddRoundedIcon /> {option.title ?? 'Add New'}</>; // this throws an MUI warning for not returning a string
        }

        // console.log(option, options);

        // selected will just be an ID; dropdown options are objects
        const foundOption = typeof option === 'object'
          ? option
          : options.find(opt => opt.id === option && (!isPolymorphic || opt.__endpoint === valueEndpoint));

        // this typically happens when we load the input before selectedObject is specified
        if(typeof foundOption === 'undefined') {
            return 'N/A';
        }

        // NOTE: If you're seeing the Fix getOptionLabel, be sure to check the
        // hydrationData implementation in ObjectRelationshipTableEdit to
        // make sure there are no other object keys that need to be handled

        return foundOption?.name ?? foundOption?.address ?? 'Fix getOptionLabel';
    }

    const groupByProp = restProps?.groupBy
      ?? (isPolymorphic ? (option) => morphLabelForEndpoint(option?.__endpoint) : undefined);

    const sortOptions = (a, b) => {
        // sort by group first to ensure there are no duplicate groups
        if(typeof groupByProp === 'function') {
          try {
            const groupOrder = groupByProp(a).localeCompare(groupByProp(b));
            if(groupOrder !== 0) {
              return groupOrder;
            }
          } catch(e) {
            //
          }
        }

        if(a.hasOwnProperty('name')) {
            return a.name.localeCompare(b.name);
        }
        return 0;
    }

    // make sure value is an array if we're in multiple mode
    if(restProps?.multiple === true) {
        if(!Array.isArray(value)) {
            value = value ? [value] : [];
        }
    }

    let extraProps = {};
    if(restProps?.multiple === true) {
      // If you set this when not multiple, the selected value will never populate
      extraProps.inputValue = searchTerm ?? '';
    }

    return (<>
        <Autocomplete
          ref={ref}
          // Base config / events
          // sx={{ width: '200px' }}
          popupIcon={<UnfoldMoreRoundedIcon/>}
          endDecorator={
            isSearching || isHydrating ? (
              <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
            ) : null
          }
          placeholder="Choose..."
          noOptionsText={!debouncedSearchTerm.length ? 'Type to search...' : 'No results'}
          open={open && !createOpen}
          onOpen={(e) => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          getOptionKey={getOptionKey}
          getOptionLabel={getOptionLabel}
          options={options.sort(sortOptions)}
          loading={isSearching || isHydrating}
          value={value}
          openOnFocus
          blurOnSelect={restProps?.multiple !== true} // this is required for the create modal to function

          // Search-specific
          // inputValue={searchTerm ?? ''} // DO NOT SET THIS PROP HERE. USE extraProps
          onInputChange={(event, newValue, reason) => {
            // todo: figure out why this always passes reset
            if(reason != 'reset') {
              setSearchTerm(newValue);
            }
          }}
          onBlur={() => {
            setSearchTerm('');
            try {
              onBlur()
            } catch(e) {
              //
            }
          }}

          onChange={handleChange}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            // add the + create button(s)
            if(params.inputValue !== '' && canCreate) {
              endpoints.map(_endpoint => {
                if(!supportedQuickCreateObjects.hasOwnProperty(_endpoint)) {
                  return;
                }

                filtered.push({
                    inputValue: params.inputValue,
                    __endpoint: _endpoint,
                    title: isPolymorphic
                      ? `Add "${params.inputValue}" as a ${supportedQuickCreateObjects[_endpoint].label}`
                      : `Add "${params.inputValue}"`,
                });
              });
            }

            return isPolymorphic ? filtered.sort(sortOptions) : filtered;
          }}

          isOptionEqualToValue={(option, value) => {
            if(isPolymorphic && restProps?.multiple !== true) {
                return option.id === value && option.__endpoint === valueEndpoint;
            }
            if(restProps?.multiple === true) {
                return (
                  option.id === value?.id // objects
                  || option.id === value // array of IDs
                );
            }
            return option.id === value;
          }}

          {...extraProps}
          {...restProps}
          groupBy={groupByProp}
        />

        <CreateModal
          key={createEndpoint}
          open={createOpen}
          setOpen={setCreateOpen}
          endpoint={createEndpoint}
          value={createInitialValue}
          afterCreate={afterCreateNew}
        />
    </>);
});

export default SearchSelect;
